import React, { useCallback, useEffect, useRef, useState } from 'react';
import { FlaskConical, Loader2, Play, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';

const C = { navy: '#0D1B2A', blue: '#1A4C7C', slate: '#5C6B7A', green: '#1D9E75', amber: '#B27516', red: '#E24B4A', bg: '#F4F6F9' };
const VERDICT = { pass: C.green, blocked: C.red, 'trial required': C.amber, 'not trialled': C.amber, 'no pack': C.slate, 'no adapter': C.slate };

function Mix({ mix }) {
  if (!mix) return null;
  return (
    <span className="font-mono text-[10px] break-words" style={{ color: C.slate }}>
      {Object.entries(mix).map(([k, v]) => `${k} ${v.cases}/${v.target_pct}%`).join(' · ')}
    </span>
  );
}

function AgentRow({ a, onOpen }) {
  const g = a.last_run?.gate;
  const codeOnly = a.pack && a.pack.by_kind && !a.pack.by_kind.model;
  const minorFailing = a.last_run && !g?.blocked && a.last_run.totals.failed > 0;
  return (
    <tr className="border-t align-top" style={{ borderColor: 'rgba(136,150,168,0.15)' }} data-testid={`evals-agent-${a.agent}`}>
      <td className="py-2 pr-3 break-words whitespace-normal">
        <p className="font-semibold" style={{ color: C.navy }}>{a.agent}</p>
        <p className="text-[10px] font-mono break-all" style={{ color: C.slate }}>{a.sites.join(', ') || 'no pinned site'}</p>
      </td>
      <td className="py-2 pr-3 text-[10px] break-words whitespace-normal" style={{ color: C.slate }}>
        {a.pack ? (<>
          <span className="font-mono" style={{ color: a.pack.min_cases_met ? C.green : C.amber }}>{a.pack.cases} cases</span>
          {' · '}{Object.entries(a.pack.by_severity).map(([k, v]) => `${v} ${k}`).join(', ')}{' · '}{Object.entries(a.pack.by_kind).map(([k, v]) => `${v} ${k}`).join(', ')}
          <br /><Mix mix={a.pack.mix} />
          {a.autonomy_note && <><br /><span style={{ color: a.pack.min_cases_met ? C.green : C.amber }}>{a.autonomy_note}</span></>}
        </>) : <span style={{ color: C.slate }}>no pack under evals/ — cannot be trialled</span>}
      </td>
      <td className="py-2 pr-3 text-[10px] font-mono break-words whitespace-normal" style={{ color: C.slate }}>
        {a.last_run ? (<>
          <button onClick={() => onOpen(a.last_run.run_id)} className="underline" style={{ color: C.blue }} data-testid={`evals-open-${a.agent}`}>{a.last_run.finished_at?.slice(0, 16).replace('T', ' ')}Z</button>
          <br />pass {g?.pass_rate ?? '—'} % · {a.last_run.totals.trials} trials · {codeOnly ? 'pass^n n/a (code only)' : `pass^${a.last_run.trials} critical ${g?.passn_critical?.passed}/${g?.passn_critical?.of}`}
          <br />{a.last_run.tokens?.calls ?? 0} calls · {((a.last_run.tokens?.input_tokens || 0) + (a.last_run.tokens?.output_tokens || 0)).toLocaleString()} tokens{a.last_run.model_available ? '' : ' · code only'}
          {minorFailing && <><br /><span style={{ color: C.amber }} data-testid={`evals-minor-${a.agent}`}>{a.last_run.totals.failed} minor case{a.last_run.totals.failed > 1 ? 's' : ''} failing — reported, not gated</span></>}
        </>) : 'never run'}
      </td>
      <td className="py-2 pl-2 text-right break-words whitespace-normal">
        <span className="inline-block px-2 py-0.5 rounded-sm text-[10px] font-semibold text-white" style={{ background: VERDICT[a.verdict] || C.slate }} data-testid={`evals-verdict-${a.agent}`}>{a.verdict}</span>
        <p className="text-[10px] mt-1" style={{ color: C.slate }} data-testid={`evals-why-${a.agent}`}>{a.why}</p>
      </td>
    </tr>
  );
}

function RunDetail({ run, onClose }) {
  return (
    <div className="mt-3 rounded-sm p-3 text-[11px]" style={{ background: C.bg }} data-testid="evals-run-detail">
      <div className="flex items-start justify-between gap-2">
        <p className="font-semibold" style={{ color: C.navy }}>{run.agent} · run {run.run_id.slice(0, 8)} · {run.gate.verdict} · pass {run.gate.pass_rate} % over {run.totals.trials} trials · {run.gate.drop_points == null ? 'no previous trial' : run.gate.drop_points < 0 ? `up ${Math.abs(run.gate.drop_points)} pts on the previous trial` : `drop ${run.gate.drop_points} pts (gate > ${run.gate.one_case_points}; 2-pt rule ${run.gate.two_point_rule_tripped ? 'tripped' : 'clear'})`}</p>
        <button onClick={onClose} className="text-[10px] underline" style={{ color: C.slate }} data-testid="evals-run-close">close</button>
      </div>
      <p className="text-[10px] font-mono mt-1" style={{ color: C.slate }}>pins: {Object.entries(run.pins).map(([s, p]) => `${s} = ${p.model} ${p.prompt_version}`).join(' · ')}</p>
      {run.gate.reasons.length > 0 && <ul className="mt-1 list-disc ml-4" style={{ color: C.red }}>{run.gate.reasons.map((r, i) => <li key={i}>{r}</li>)}</ul>}
      <table className="w-full mt-2 text-[10px]">
        <thead><tr style={{ color: C.slate }}><th className="text-left">case</th><th className="text-left">sev</th><th className="text-left">kind</th><th className="text-left">trials</th><th className="text-left">result</th></tr></thead>
        <tbody>{run.cases.map(c => (
          <tr key={c.case_id} className="border-t" style={{ borderColor: 'rgba(136,150,168,0.15)' }} data-testid={`evals-case-${c.case_id}`}>
            <td className="py-1 pr-2 whitespace-normal" style={{ color: C.navy }}>{c.case_id} <span style={{ color: C.slate }}>{c.name}</span></td>
            <td className="py-1 pr-2 font-mono">{c.severity}</td><td className="py-1 pr-2 font-mono">{c.kind}</td>
            <td className="py-1 pr-2 font-mono">{c.trials.length}{c.pass_rate != null ? ` · ${c.pass_rate} %` : ''}</td>
            <td className="py-1 whitespace-normal" style={{ color: c.status === 'skipped' ? C.amber : c.passed_all ? C.green : C.red }}>{c.status === 'skipped' ? c.skip_reason : c.passed_all ? 'pass' : (c.trials.find(t => !t.passed)?.details || []).join('; ')}</td>
          </tr>))}
        </tbody>
      </table>
      <p className="text-[10px] mt-2" style={{ color: C.slate }}>{run.judge.note}</p>
    </div>
  );
}

// Assurance Step 3 — every model-driven agent trialled against its case pack before it goes live.
export default function EvalsCard({ api }) {
  const { user } = useAuth();
  const canRun = ['platform_owner', 'mga_admin'].includes(user?.role);
  const [st, setSt] = useState(null);
  const [stale, setStale] = useState(false);
  const [run, setRun] = useState(null);
  const [busy, setBusy] = useState(false);
  const pollRef = useRef(null);
  const load = useCallback(() => api.get('/assurance/evals').then(r => { setSt(r.data); setStale(false); }).catch(() => setStale(true)), [api]);
  useEffect(() => { load(); return () => { if (pollRef.current) clearInterval(pollRef.current); }; }, [load]);
  const runNow = () => {
    setBusy(true);
    api.post('/assurance/evals/run', {}).then(r => {
      toast[r.data.status === 'accepted' ? 'success' : 'warning'](`Evals: ${r.data.status}${r.data.stop_reason ? ` — ${r.data.stop_reason}` : ' — trialling every pack (a few minutes)'}`);
      if (r.data.status !== 'accepted') return;
      if (pollRef.current) clearInterval(pollRef.current);
      let n = 0;
      pollRef.current = setInterval(() => {
        n += 1;
        api.get('/jobs/evals/runs', { params: { limit: 5 } }).then(res => {
          const row = (res.data || []).find(x => x.run_id === r.data.run_id);
          if ((row && !['accepted', 'running'].includes(row.status)) || n > 60) { clearInterval(pollRef.current); pollRef.current = null; load(); if (row) toast.info(`Evals finished — ${row.status}${row.result?.blocked?.length ? `; blocked: ${row.result.blocked.join(', ')}` : ''}`); }
        }).catch(() => {});
      }, 8000);
    }).catch(e => toast.error(e?.response?.data?.detail || 'Not started')).finally(() => setBusy(false));
  };
  const open = id => api.get(`/assurance/evals/runs/${id}`).then(r => setRun(r.data)).catch(() => toast.error('Run not found'));
  if (!st) return stale ? <div className="rounded-sm border bg-white p-4 mt-4 text-[11px]" style={{ borderColor: 'rgba(136,150,168,0.25)', color: C.amber }} data-testid="evals-card">Evals — the status could not be loaded; it will retry on the next refresh.</div> : null;
  return (
    <div className="rounded-sm border bg-white p-4 mt-4 whitespace-normal" style={{ borderColor: 'rgba(136,150,168,0.25)' }} data-testid="evals-card">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h3 className="text-sm font-semibold flex items-center gap-2" style={{ color: C.navy }}><FlaskConical className="w-4 h-4" />Evals — trialled before live</h3>
          <p className="text-[11px] mt-1 max-w-3xl" style={{ color: C.slate }}>
            Case packs under <span className="font-mono">evals/underaiter/&lt;agent&gt;/</span> (code-owned). Code checks decide; model cases run {st.trials_default} trials; the gate is case-granular — {st.gate_rule}. The CLI exit code blocks the merge in CI.
          </p>
          <p className="text-[10px] mt-1 font-mono" style={{ color: st.model_available ? C.green : C.amber }} data-testid="evals-model-available">
            model {st.model_available ? 'available — trials run here' : 'not available — code checks only'} · blocked: {st.blocked.length ? st.blocked.join(', ') : 'none'} · trial required: {st.trial_required.length ? st.trial_required.join(', ') : 'none'}{stale ? ' · last refresh failed — showing the previous reading' : ''}
          </p>
        </div>
        {canRun && (
          <button onClick={runNow} disabled={busy} className="px-3 py-1.5 rounded-sm text-[11px] font-semibold text-white flex items-center gap-1 disabled:opacity-50" style={{ background: C.navy }} data-testid="evals-run-btn">
            {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Play className="w-3 h-3" />}Run evals
          </button>
        )}
      </div>
      <table className="w-full text-[11px] mt-3 table-fixed">
        <thead><tr style={{ color: C.slate }}><th className="text-left py-1 w-[17%]">Agent · sites</th><th className="text-left py-1 w-[37%]">Pack</th><th className="text-left py-1 w-[24%]">Last trial</th><th className="text-right py-1 w-[22%]"><ShieldCheck className="w-3 h-3 inline" /> Live?</th></tr></thead>
        <tbody>{st.agents.map(a => <AgentRow key={a.agent} a={a} onOpen={open} />)}</tbody>
      </table>
      {run && <RunDetail run={run} onClose={() => setRun(null)} />}
    </div>
  );
}
